import React, { useState, useEffect } from 'react';
import { User } from 'lucide-react';
import { useTranslation } from '../i18n';

interface FloatingUserButtonProps {
  authUser?: {
    name?: string;
    email?: string;
    phone?: string;
    role?: string;
    permissions?: string[];
  } | null;
  onClick: () => void;
}

const isEmailLike = (value?: string) => Boolean(value?.includes('@'));
const isPlanLikeName = (value?: string) => /^vip[\s_-]*\d+$/i.test((value || '').trim());
const getEmailPrefix = (email?: string) => (email || '').split('@')[0]?.trim() || '';

const getPreferredDisplayName = (authUser?: FloatingUserButtonProps['authUser']) => {
  const accountName = authUser?.name?.trim();
  if (accountName && !isEmailLike(accountName) && !isPlanLikeName(accountName)) {
    return accountName;
  }
  return getEmailPrefix(authUser?.email) || authUser?.phone?.trim() || '';
};

export const FloatingUserButton: React.FC<FloatingUserButtonProps> = ({
  authUser,
  onClick,
}) => {
  const { t } = useTranslation();
  const [userName, setUserName] = useState('');
  const [userAvatar, setUserAvatar] = useState('');

  useEffect(() => {
    const userKey = authUser?.email || 'default';
    const savedUserName = localStorage.getItem(`user_name_${userKey}`);
    setUserName(
      savedUserName && !isEmailLike(savedUserName) && !isPlanLikeName(savedUserName)
        ? savedUserName
        : getPreferredDisplayName(authUser) || t('Authenticated User'),
    );
    setUserAvatar(localStorage.getItem(`user_avatar_${userKey}`) || '');
  }, [authUser, t]);

  useEffect(() => {
    const handleSettingsUpdated = () => {
      const userKey = authUser?.email || 'default';
      const savedUserName = localStorage.getItem(`user_name_${userKey}`);
      if (savedUserName) setUserName(savedUserName);
      setUserAvatar(localStorage.getItem(`user_avatar_${userKey}`) || '');
    };
    window.addEventListener('settings_updated', handleSettingsUpdated);
    return () => window.removeEventListener('settings_updated', handleSettingsUpdated);
  }, [authUser?.email]);

  if (!authUser) return null;

  return (
    <div className="absolute bottom-4 left-4 z-20 animate-[fade-in_200ms_ease-out]">
      <button
        type="button"
        onClick={onClick}
        title={userName}
        className="relative flex h-9 w-9 items-center justify-center rounded-full border-2 border-transparent bg-gradient-to-tr from-cyan-600 to-blue-600 p-0.5 shadow-md transition-all hover:shadow-lg hover:scale-105 active:scale-95"
      >
        <div className="relative flex h-full w-full items-center justify-center rounded-full bg-[var(--color-bg-card)]">
          {userAvatar ? (
            <img src={userAvatar} alt={t('Avatar')} className="h-full w-full rounded-full object-cover" />
          ) : (
            <User size={16} className="text-gray-500" />
          )}
          <div className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-[var(--color-bg-card)] bg-emerald-500" />
        </div>
      </button>
    </div>
  );
};

export default FloatingUserButton;
